import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import { IconColor, IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import Icon from '@aurora/shared-client/components/common/Icon/Icon';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import UserAvatar from '@aurora/shared-client/components/users/UserAvatar/UserAvatar';
import UserBiography from '@aurora/shared-client/components/users/UserBiography/UserBiography';
import UserRank from '@aurora/shared-client/components/users/UserRank/UserRank';
import Icons from '@aurora/shared-client/public/static/graphics/processed/enums';
import type {
  OccasionTopicMessage,
  User
} from '@aurora/shared-generated/types/graphql-schema-types';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import React from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import type {
  OccasionFeaturedGuestQuery,
  OccasionFeaturedGuestQueryVariables
} from '../../../types/graphql-types';
import UserLink from '../../users/UserLink/UserLink';
import useTranslation from '../../useTranslation';
import localStyles from './OccasionFeaturedGuest.module.pcss';
import occasionFeaturedGuestQuery from './OccasionFeaturedGuest.query.graphql';

interface Props {
  /**
   * The users to be displayed as featured guests.
   */
  users?: User[];
  /**
   * The occasion id for which featured guests are displayed.
   */
  occasionId?: string;
  /**
   * Classname applied to the outermost element.
   */
  className?: string;
  /**
   * Whether the component is used in the editorForm.
   */
  isEditMode?: boolean;
  /**
   * Callback to remove a user.
   */
  onRemove?: (user: User) => void;
}

/**
 * Occasion Featured Guest
 *
 * @author Aditi Agarwal
 */
const OccasionFeaturedGuest: React.FC<React.PropsWithChildren<Props>> = ({
  users: featuredGuests,
  className,
  isEditMode = false,
  occasionId,
  onRemove
}) => {
  const cx = useClassNameMapper(localStyles);
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.OCCASION_FEATURED_GUEST
  );

  const { data: occasionFeaturedGuestData } = useQueryWithTracing<
    OccasionFeaturedGuestQuery,
    OccasionFeaturedGuestQueryVariables
  >(module, occasionFeaturedGuestQuery, {
    variables: {
      id: occasionId
    },
    skip: !occasionId
  });

  if (textLoading) {
    return null;
  }

  const occasionFeaturedGuests = occasionId
    ? (occasionFeaturedGuestData?.message as OccasionTopicMessage)?.occasionData?.featuredGuests
    : featuredGuests;

  /**
   * Renders the featured guests data.
   *
   * @param user featured guest for an occasion
   */
  function RenderFeaturedGuest({ user }: { user }): React.ReactElement {
    return (
      user && (
        <div className={cx('d-flex align-items-center')} key={user?.id}>
          <UserLink user={user} className={cx('align-self-start')} openInNewTab={isEditMode}>
            <UserAvatar user={user} size={IconSize.FROM_CSS} className={cx('lia-user-icon')} />
          </UserLink>
          <div
            className={cx('lia-footer-details-wrapper', {
              'lia-footer-details-edit-wrap': isEditMode
            })}
          >
            <div className={cx('lia-footer-details', { 'lia-footer-edit': isEditMode })}>
              <div className={cx('d-flex align-items-center')}>
                <UserLink
                  className={cx('lia-g-divider lia-author-login')}
                  user={user}
                  openInNewTab={isEditMode}
                >
                  {user?.login}
                </UserLink>
                {user?.uid !== -1 && <UserRank className={cx('lia-g-ml-5')} user={user} />}
              </div>
              {user?.uid !== -1 && !user?.deleted && (
                <UserBiography
                  className={cx('lia-footer-description')}
                  user={user}
                  clampLines={5}
                />
              )}
            </div>
            {!isEditMode ? (
              user?.uid !== -1 &&
              !user?.deleted && (
                <div className={cx('lia-action')}>
                  <UserLink
                    user={user}
                    useHoverCard={false}
                    passHref
                    className={cx('btn btn-secondary text-nowrap')}
                  >
                    {formatMessage('userProfile')}
                  </UserLink>
                </div>
              )
            ) : (
              <Button
                variant={ButtonVariant.UNSTYLED}
                onClick={() => onRemove(user)}
                className={cx('lia-trash-icon')}
                title={formatMessage('removeGuest')}
              >
                <Icon icon={Icons.TrashIcon} size={IconSize.PX_16} color={IconColor.GRAY_900} />
              </Button>
            )}
          </div>
        </div>
      )
    );
  }

  return occasionFeaturedGuests?.length > 0 ? (
    <article
      className={cx('lia-footer-wrapper', { 'lia-edit-wrapper': isEditMode }, className)}
      data-testid="OccasionFeaturedGuest"
    >
      {!isEditMode && <h6>{formatMessage('featuredGuest')}</h6>}
      {occasionFeaturedGuests?.map(guest => (
        <RenderFeaturedGuest key={guest?.id} user={guest} />
      ))}
    </article>
  ) : null;
};

export default OccasionFeaturedGuest;
